import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { firstValueFrom, interval } from "rxjs";
import JSZip from "jszip";
import { MatButton } from "@angular/material/button";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressBar } from "@angular/material/progress-bar";
import { DataDetailDialogComponent } from "../../shared/data-detail-dialog/data-detail-dialog.component";
import { CardHeaderComponent } from "../../shared/card-header/card-header.component";
import { TodoListComponent } from "../../shared/todo-list/todo-list.component";
import { OutputRefChipComponent } from "../../inline/output-ref-chip/output-ref-chip.component";
import { BackgroundJobTrackerService } from "../../../../services/background-job-tracker.service";
import { DebugModeService } from "../../../../services/debug-mode.service";
import { MapInteractionService } from "../../../../services/map-interaction.service";
import { FileUploadService } from "../../../../services/file-upload.service";
import { FileReaderService } from "../../../../../../viewer-components/services/readers/file-reader.service";
import type {
  BackgroundJobBlockData,
  BackgroundJobSnapshot,
  JobStatus,
  OutputRef,
  PhaseDetail,
  PhaseStats,
  PipelinePhaseEventStatus,
  PipelinePhaseStep,
  PipelineSummary,
  ProviderConfig,
} from "../../../../model/block.types";

import DataMapper from "@vision/core-map-api/src/clients/mappers/DataMapper.js";
import { SearchUtilsService } from "../../../../../../compound-components/services/search/utils/search-utils.service";
import { GeoService } from "../../../../../../viewer-components/services/internal-services/geo.service";

const ROOF_SCORE_COLORS: Readonly<Record<number, string>> = {
  1: "#DE3509",
  2: "#EE7105",
  3: "#FEAC00",
  4: "#A2E412",
  5: "#34B37E",
};

function preprocessPipelineCollection(collection: any, layerGroup: string | undefined): any {
  if (!collection?.features?.length) return collection;
  if (layerGroup === "properties") {
    const expanded: any[] = [];
    for (const f of collection.features) {
      const normalized = DataMapper.property(f);
      normalized.structures?.sort((a: any, b: any) => {
        if (a.primaryStructure && !b.primaryStructure) return -1;
        if (!a.primaryStructure && b.primaryStructure) return 1;
        return (b.footPrint?.area ?? 0) - (a.footPrint?.area ?? 0);
      });
      // Parcel feature — geometría original, IPropertyResponse completo para el popup
      expanded.push({
        ...f,
        properties: { ...normalized, _footprint_type: "parcel", _pipeline_layer_group: "properties" },
      });
      // Una feature por estructura con su propia geometría, color de roof score y número de índice
      for (const [i, structure] of (normalized.structures ?? []).entries()) {
        if (!structure.footPrint?.wkt) continue;
        const structureGeom = GeoService.wktPolygonToFeature(structure.footPrint.wkt)?.geometry;
        if (!structureGeom) continue;
        expanded.push({
          type: "Feature",
          id: crypto.randomUUID(),
          geometry: structureGeom,
          properties: {
            ...normalized,
            _footprint_type: "structure",
            _pipeline_layer_group: "properties",
            _roof_color: ROOF_SCORE_COLORS[structure.roof_condition?.value] ?? "#9e9e9e",
            _roof_score: structure.roof_condition?.value ?? null,
            _structure_index: i + 1,
          },
        });
      }
    }
    collection.features = expanded;
  } else if (layerGroup === "damage") {
    collection.features = collection.features.map((f: any) => ({
      ...f,
      properties: DataMapper.damageStructureProperties(f),
    }));
  } else if (layerGroup === "embeddings") {
    // Normalize similarity scores across the full collection and assign per-feature color.
    // Centroid computation is deferred to addGroupLayer() which converts to WGS84 first.
    const features = collection.features;
    const similarities: number[] = features.map((f: any) => f.properties?.similarity ?? f.properties?.score ?? 0);
    const minSim = Math.min(...similarities);
    const maxSim = Math.max(...similarities);
    const range = maxSim - minSim || 1;
    collection.features = features.map((f: any) => {
      const raw = f.properties?.similarity ?? f.properties?.score ?? 0;
      const score = Math.max(0, Math.min(1, (raw - minSim) / range));
      return {
        ...f,
        properties: {
          ...f.properties,
          score,
          scoreColor: SearchUtilsService.similarityColor(score),
          _pipeline_layer_group: "embeddings",
        },
      };
    });
  } else if (layerGroup === "geofence") {
    collection.features = collection.features.map((f: any) => ({
      ...f,
      properties: { ...f.properties, _pipeline_layer_group: "geofence" },
    }));
  } else if (layerGroup === "aoi") {
    collection.features = collection.features.map((f: any) => ({
      ...f,
      properties: { ...f.properties, _pipeline_layer_group: "aoi" },
    }));
  }
  return collection;
}

const PIPELINE_LAYER_LABELS: Readonly<Record<string, string>> = {
  merged: "Pipeline Results",
  embeddings: "Embedding Tiles",
  properties: "Property Footprints",
  damage: "Damage Assessment",
  vlm: "VLM Analysis",
  geofence: "Analysis Boundary",
  aoi: "Zone of Interest",
  other: "Other Results",
};

const OUTPUT_TYPE_TO_LAYER_GROUP: Readonly<Record<string, string>> = {
  vlm_geojson: "vlm",
  properties_geojson: "properties",
  damage_geojson: "damage",
  geojson_report: "merged",
  embeddings_geojson: "embeddings",
  geofence_geojson: "geofence",
  aoi_geojson: "aoi",
};

/** Intermediate per-phase outputs — shown only in debug mode. */
const RAW_OUTPUT_TYPES = new Set([
  "vlm_geojson",
  "properties_geojson",
  "damage_geojson",
  "embeddings_geojson",
  "geofence_geojson",
  "aoi_geojson",
]);

/**
 * Displays a task-progress card (background job) in the message content area.
 * Reads live snapshots from BackgroundJobTrackerService — no polling logic here.
 * Polling is started by ContentDispatcherComponent when the block first appears.
 * The host id binding allows the panel to scroll to this card.
 */
@Component({
  selector: "app-task-list-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { "[id]": "'bg-job-' + block().job_id" },
  imports: [
    MatButton,
    MatCard,
    MatCardContent,
    MatIcon,
    MatProgressBar,
    NgTemplateOutlet,
    CardHeaderComponent,
    TodoListComponent,
    OutputRefChipComponent,
    DataDetailDialogComponent,
  ],
  templateUrl: "./task-list-card.component.html",
  styleUrl: "./task-list-card.component.scss",
})
export class TaskListCardComponent {
  readonly block = input.required<BackgroundJobBlockData>();

  readonly #tracker = inject(BackgroundJobTrackerService);

  readonly #map = inject(MapInteractionService);

  readonly #fileUpload = inject(FileUploadService);

  readonly #fileReader = inject(FileReaderService);

  readonly #dialog = inject(MatDialog);

  protected readonly debugMode = inject(DebugModeService);

  readonly #destroyRef = inject(DestroyRef);

  // Cache the last non-null snapshot so the card never regresses to the
  // "Starting" skeleton after BackgroundJobTrackerService removes the job
  // from its map on completion.
  private readonly _snapshot = signal<BackgroundJobSnapshot | null>(null);

  protected readonly snapshot = this._snapshot.asReadonly();

  protected readonly elapsedSeconds = signal<number>(0);

  protected readonly addingToMap = signal<boolean>(false);

  protected readonly addedToMap = signal<boolean>(false);

  protected readonly downloadingZip = signal<boolean>(false);

  protected readonly geojsonRefs = computed<OutputRef[]>(() => {
    return (this.snapshot()?.output_refs ?? []).filter((r) => {
      const cat = r.content_category?.toLowerCase();
      const mime = r.content_type?.toLowerCase() ?? "";
      const ext = r.filename.split(".").pop()?.toLowerCase() ?? "";
      return cat === "geojson" || mime.includes("geo+json") || ext === "geojson";
    });
  });

  /** Output refs always visible — final results (geojson_report, csv, etc.) and any unrecognized types. */
  protected readonly visibleRefs = computed<OutputRef[]>(() =>
    (this.snapshot()?.output_refs ?? []).filter((r) => !RAW_OUTPUT_TYPES.has(r.output_type ?? "")),
  );

  /** Intermediate per-phase outputs — shown only in debug mode. */
  protected readonly rawRefs = computed<OutputRef[]>(() =>
    (this.snapshot()?.output_refs ?? []).filter((r) => RAW_OUTPUT_TYPES.has(r.output_type ?? "")),
  );

  protected readonly canAddToMap = computed<boolean>(
    () => this.snapshot()?.run_status === "success" && this.geojsonRefs().length > 0 && !this.addedToMap(),
  );

  /** Provider selections per phase — extracted from state_debug, available in debug mode only. */
  protected readonly debugProviders = computed<Record<string, ProviderConfig[]>>(
    () => (this.snapshot()?.state_debug?.providers as Record<string, ProviderConfig[]>) ?? {},
  );

  /** Pipeline summary — prefers state_debug (polling), falls back to block (patched on completion). */
  protected readonly debugPipelineSummary = computed<PipelineSummary | null>(
    () =>
      (this.snapshot()?.state_debug?.pipeline_summary as PipelineSummary | undefined) ??
      this.snapshot()?.block.pipeline_summary ??
      null,
  );

  /** Returns providers selected for a given phase name. Phase names match provider keys (e.g. "discovery", "enrich"). */
  protected getPhaseProviders(phaseName: string): ProviderConfig[] {
    return this.debugProviders()[phaseName] ?? [];
  }

  /** Returns PhaseStats for phases that have them (currently: enrich). */
  protected getPhaseStats(phaseName: string): PhaseStats | null {
    const summary = this.debugPipelineSummary();
    if (!summary) return null;
    return (summary as unknown as Record<string, PhaseStats | undefined>)[`${phaseName}_stats`] ?? null;
  }

  /**
   * Live status for a phase row's icon/spinner. In practice `pipeline_phase_status` is only
   * observed being sent for the LAST phase (`report`, once, at job end) — discovery/enrich/
   * deep_analysis never get one. So this leans on whatever signal each phase actually has:
   * - discovery/report: derived from their own `pipeline_phase_step` nodes — running if any
   *   step is still running, completed once all seen steps are done (no phase-level event
   *   needed), never permanently "running" the way a bare steps.length>0 check would be.
   * - deep_analysis: has no step/phase_status of its own either, but its tool-call/message/
   *   todo CUSTOM events are unambiguous proof it's active — treated as "running" while any
   *   of that activity exists.
   * - anything else: `job_progress.current_phase` (STATE_SNAPSHOT), then `fallback`.
   */
  protected getEffectivePhaseStatus(
    phaseName: string,
    fallback: JobStatus,
    snap: BackgroundJobSnapshot,
  ): JobStatus | PipelinePhaseEventStatus {
    const overlay = snap.phaseStates[phaseName]?.status;
    if (overlay) return overlay;

    const steps = snap.phaseSteps[phaseName];
    if (steps && steps.length > 0) {
      if (steps.some((s) => s.status === "running")) return "running";
      if (steps.some((s) => s.status === "error")) return "error";
      return "completed";
    }

    if (
      this.isDeepAnalysisPhase(phaseName) &&
      (snap.subAgentTodos.length > 0 || !!snap.deepAnalysis.toolCall || !!snap.deepAnalysis.message)
    ) {
      return "running";
    }

    if (snap.progress?.current_phase === phaseName) return "running";
    return fallback;
  }

  /** Current `pipeline_phase_status` message for a phase, or null if none arrived yet. */
  protected getPhaseMessage(phaseName: string, snap: BackgroundJobSnapshot): string | null {
    return snap.phaseStates[phaseName]?.message || null;
  }

  /** Internal step progress for a phase (discovery/report only) from `pipeline_phase_step`. */
  protected getPhaseSteps(phaseName: string, snap: BackgroundJobSnapshot): PipelinePhaseStep[] {
    return snap.phaseSteps[phaseName] ?? [];
  }

  /** Ordered phase names from `job_progress` — for index-based lookups in the template. */
  protected getPhaseNamesFromProgress(snap: BackgroundJobSnapshot): string[] {
    return snap.progress!.phases.map((p) => p.name);
  }

  /**
   * True once any phase after `index` (by position in `names`) is already running/completed
   * per `getEffectivePhaseStatus` (any signal — overlay, steps, or deep_analysis activity, not
   * just the raw overlay, since most phases never get their own `pipeline_phase_status`) — used
   * to force earlier phases to show as completed and drop their spinner even if their own
   * closing event never arrived. A real error on the phase itself is never overridden by this
   * (checked by the caller before using this).
   */
  protected isPhaseForcedComplete(index: number, names: string[], snap: BackgroundJobSnapshot): boolean {
    for (let j = index + 1; j < names.length; j++) {
      const status = this.getEffectivePhaseStatus(names[j], "pending", snap);
      if (status === "running" || status === "completed" || status === "skipped") return true;
    }
    return false;
  }

  /**
   * True for the sole isolated sub-agent phase — `deep_analysis_tool_call`/
   * `deep_analysis_message_chunk` events carry no phase field of their own since only this
   * phase uses that channel, so the match is by the fixed phase name.
   */
  protected isDeepAnalysisPhase(phaseName: string): boolean {
    return phaseName === "deep_analysis";
  }

  constructor() {
    effect(() => {
      const live = this.#tracker.getSnapshot(this.block().job_id);
      if (live !== null) {
        this._snapshot.set(live);
      }
    });

    // Elapsed timer: starts once the first snapshot with started_at arrives,
    // stops when the job reaches a terminal state.
    interval(1000)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const snap = this.snapshot();
        const startedAt = snap?.progress?.started_at;
        if (!startedAt || this.isTerminal(snap!)) return;
        this.elapsedSeconds.set(Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
      });
  }

  protected formatPhaseDuration(startedAt: string | null, completedAt: string | null): string {
    if (!startedAt || !completedAt) return "";
    const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
    return `${(ms / 1000).toFixed(1)}s`;
  }

  protected formatElapsed(seconds: number): string {
    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }

  protected hasPhaseError(snap: BackgroundJobSnapshot): boolean {
    return snap.progress?.phases.some((p) => !!p.error_message) ?? false;
  }

  protected isTerminal(snap: BackgroundJobSnapshot): boolean {
    return snap.run_status === "success" || snap.run_status === "error" || snap.run_status === "timeout";
  }

  /** True when the job is terminal and the phase was never started (no provider / not applicable). */
  protected isPhaseSkipped(phase: PhaseDetail, snap: BackgroundJobSnapshot): boolean {
    return this.isTerminal(snap) && phase.status !== "completed" && phase.status !== "error";
  }

  protected addAllToMap(): void {
    if (this.addingToMap()) return;
    this.addingToMap.set(true);

    const refs = this.geojsonRefs();
    const displayName = this.snapshot()?.block.display_name;
    const name = displayName ?? `Pipeline ${new Date().toISOString().slice(0, 16).replace("T", " ")}`;

    Promise.all(
      refs.map((ref) => {
        const extras = { filename: ref.filename, size_bytes: 0 };
        return firstValueFrom(
          this.#fileUpload.fetchFileContent(ref.url!, ref.content_type ?? "application/geo+json", extras),
        ).then((result) => {
          if (result.type !== "geojson") return null;
          const prepared = this.#fileReader.prepareGeoJson(result.geojson as any, ref.filename);
          if (!prepared.data) {
            console.error("TaskListCardComponent: GeoJSON preparation failed", ref.filename, prepared.errors);
            return null;
          }
          return prepared.data;
        });
      }),
    )
      .then((results) => {
        const layers = results
          .map((collection, i) => {
            if (!collection) return null;
            const group = refs[i].output_type ? OUTPUT_TYPE_TO_LAYER_GROUP[refs[i].output_type] : undefined;
            const processedCollection = preprocessPipelineCollection(collection, group);
            return {
              collection: processedCollection,
              label: group ? PIPELINE_LAYER_LABELS[group] : undefined,
              visible: true,
              layer_group: group,
            };
          })
          .filter(Boolean) as { collection: any; label?: string; visible: boolean; layer_group?: string }[];
        if (layers.length) {
          this.#map.addGroupLayer(name, layers);
          const geofenceLayer = layers.find((l) => l.layer_group === "geofence");
          if (geofenceLayer) {
            const bbox: number[] = geofenceLayer.collection?.properties?.bbox;
            if (bbox?.length === 4) {
              this.#map.zoomToExtent(bbox[0], bbox[1], bbox[2], bbox[3]);
            }
          }
          this.addedToMap.set(true);
        }
      })
      .catch((err) => console.error("TaskListCardComponent: addAllToMap failed", err))
      .finally(() => this.addingToMap.set(false));
  }

  protected downloadAllAsZip(): void {
    if (this.downloadingZip()) return;
    this.downloadingZip.set(true);
    const refs = this.geojsonRefs();
    const baseName = this.snapshot()?.block.display_name ?? "pipeline";

    const zip = new JSZip();
    Promise.all(
      refs.map((ref) => {
        const extras = { filename: ref.filename, size_bytes: 0 };
        return firstValueFrom(
          this.#fileUpload.fetchFileContent(ref.url!, ref.content_type ?? "application/geo+json", extras),
        ).then((result) => {
          if (result.type !== "geojson") return;
          zip.file(ref.filename, JSON.stringify(result.geojson, null, 2));
        });
      }),
    )
      .then(() => zip.generateAsync({ type: "blob" }))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${baseName}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((err) => console.error("TaskListCardComponent: downloadAllAsZip failed", err))
      .finally(() => this.downloadingZip.set(false));
  }

  protected showDebugInfo(): void {
    const debug = this.snapshot()?.state_debug;
    if (!debug) return;
    this.#dialog.open(DataDetailDialogComponent, {
      data: { data: { type: "json", json: debug }, title: "Pipeline debug" },
      width: "700px",
      maxWidth: "95vw",
      minHeight: "400px",
      maxHeight: "90vh",
    });
  }
}
